<div>
    @if (metricsLoadError) {
        <div class="alert alert-warning py-2 small" role="alert">Dashboard metrics could not be loaded. Try refreshing the page.</div>
    }

    <div class="row g-3 mb-3">
        <div class="col-6 col-lg-2">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Total feedback</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.totalSubmitted }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-2">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Submitted this month</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.submittedThisMonth }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-2">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Submitted last 30 days</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.submittedLast30Days }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-2">
            <div class="card h-100 border-success border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Positive feedback</div>
                    <div class="fs-3 fw-semibold text-success">{{ metrics.kpi.positiveFeedbackCount }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-2">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Jobs with feedback</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.jobsWithFeedback }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-2">
            <div class="card h-100 border-danger border-opacity-50">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Needs JHA</div>
                    <div class="fs-3 fw-semibold text-danger">{{ metrics.kpi.requiresJhaOutstanding }}</div>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3">
        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Feedback by month</h5>
                    <p class="small text-muted">Feedback submitted over the last 6 months.</p>
                    @if (metrics.monthlyCounts.length === 0) {
                        <p class="text-muted small mb-0">No feedback in this period.</p>
                    } @else {
                        <div class="d-flex align-items-end gap-1 sf-bar-wrap">
                            @for (m of metrics.monthlyCounts; track m.label) {
                                <div class="flex-fill text-center">
                                    <div class="sf-bar rounded-top" [style.height.px]="(m.count / maxMonthlyCount()) * 120 || 4" [title]="m.label + ': ' + m.count"></div>
                                    <div class="small text-muted mt-1">{{ m.label }}</div>
                                    <div class="small fw-semibold">{{ m.count }}</div>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>

        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Top jobs by feedback</h5>
                    <p class="small text-muted">Jobs with the most feedback records.</p>
                    @if (metrics.topJobs.length === 0) {
                        <p class="text-muted small mb-0">No feedback recorded yet.</p>
                    } @else {
                        <ul class="list-unstyled mb-0">
                            @for (job of metrics.topJobs; track job.jobId) {
                                <li class="d-flex justify-content-between border-bottom py-1">
                                    <span class="text-truncate me-2">{{ job.jobName }}</span>
                                    <span class="fw-semibold">{{ job.count }}</span>
                                </li>
                            }
                        </ul>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mt-0">
        <div class="col-lg-6">
            <div class="card h-100 border-danger border-opacity-50">
                <div class="card-body">
                    <h5 class="card-title">Feedback requiring JHA</h5>
                    <div class="row g-2 mb-3">
                        <div class="col-4">
                            <div class="text-muted small text-uppercase">Needs JHA</div>
                            <div class="fs-4 fw-semibold text-danger">{{ metrics.requiringJha.needsJhaCount }}</div>
                        </div>
                        <div class="col-4">
                            <div class="text-muted small text-uppercase">JHA in progress</div>
                            <div class="fs-4 fw-semibold">{{ metrics.requiringJha.inProgressCount }}</div>
                        </div>
                        <div class="col-4">
                            <div class="text-muted small text-uppercase">Addressed this month</div>
                            <div class="fs-4 fw-semibold text-success">{{ metrics.requiringJha.addressedThisMonth }}</div>
                        </div>
                    </div>
                    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h6 class="mb-0">Awaiting a JHA</h6>
                        <span class="badge rounded-pill text-bg-danger">{{ metrics.requiringJha.outstanding.length }}</span>
                    </div>
                    @if (metrics.requiringJha.outstanding.length === 0) {
                        <p class="text-muted small mb-0">No feedback is awaiting a JHA.</p>
                    } @else {
                        <div class="sf-list-scroll">
                            <table class="table table-sm table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Job</th>
                                        <th>Event date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (item of metrics.requiringJha.outstanding; track item.feedbackId) {
                                        <tr>
                                            <td class="text-truncate">{{ item.jobName }}</td>
                                            <td class="text-nowrap">
                                                @if (item.eventDateTime) {
                                                    {{ item.eventDateTime | date: 'mediumDate' }}
                                                } @else {
                                                    <span class="text-muted small">-</span>
                                                }
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
</div>
